import { useRef, useEffect, useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Close from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

import { PageListContainer } from "../../../components/Page";
import { useConfirm } from "../../../components/feedback";
import ProjectDialog from "../../../components/GenericDialog";
import { incidentService } from "../../../services/incidentService";
import type { Incident } from "../../../types/incident";
import IncidentModel from "../model";
import editIncidentBody from "./components/EditIncidentDialogBody";
import assignIncidentBody from "./components/AssignIncidentDialogBody";
import statusIncidentBody from "./components/StatusIncidentDialogBody";
import { INCIDENT_FOLDERS, toIncidentPriorityEnum } from "../lib/constants";
import { emailRecipientFallback } from "../../../lib/incident/emailNotification";
import { whatsappRecipientFallback } from "../../../lib/incident/whatsappNotification";
import { useIncidentsWorkspace } from "./hooks/useIncidentsWorkspace";
import IncidentFolderSidebar from "./components/IncidentFolderSidebar";
import IncidentListPanel from "./components/IncidentListPanel";
import IncidentReadingPane from "./components/IncidentReadingPane";
import IncidentAdvancedFilters from "./components/IncidentAdvancedFilters";

function IncidentsWorkspace() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const confirm = useConfirm();
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  const editIncidentDialogRef = useRef<any>(null);
  const assignIncidentDialogRef = useRef<any>(null);
  const statusIncidentDialogRef = useRef<any>(null);
  const [sendingWhatsAppId, setSendingWhatsAppId] = useState<string | null>(null);
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);

  const workspace = useIncidentsWorkspace();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!workspace.selectedIncident) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (target.closest("[role='dialog'], [role='menu'], [role='listbox'], [role='option'], [role='combobox']")) {
        return;
      }

      event.preventDefault();
      workspace.moveSelection(event.key === "ArrowDown" ? 1 : -1);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [workspace.selectedIncident, workspace.moveSelection]);

  useEffect(() => {
    const handleGlobalRefresh = () => {
      workspace.fetchIncidents();
    };
    window.addEventListener("refreshDataTable", handleGlobalRefresh);
    return () => window.removeEventListener("refreshDataTable", handleGlobalRefresh);
  }, [workspace.fetchIncidents]);

  const showSnack = useCallback((opts: any) => {
    window.dispatchEvent(new CustomEvent("showGlobalNotification", { detail: opts }));
  }, []);

  const refreshAfterMutation = useCallback(() => {
    setTimeout(() => workspace.fetchIncidents(), 500);
  }, [workspace.fetchIncidents]);

  const editIncidentSubmitAction = (formJson: any) => {
    incidentService
      .updateIncident({
        id: formJson.id,
        title: formJson.title,
        description: formJson.description,
        priority: toIncidentPriorityEnum(formJson.priority),
        sourceSystemId: formJson.sourceSystemId,
        assignedTo: formJson.assignedTo || undefined,
        assignedGroup: formJson.assignedGroup || undefined,
        category: formJson.category,
        subcategory: formJson.subcategory,
        tags: formJson.tags ? formJson.tags.split(",").map((tag: string) => tag.trim()) : [],
        metadata: {},
        openSearchId: formJson.openSearchId || "",
      })
      .then(() =>
        IncidentModel.message(
          IncidentModel.SUCCESS,
          t("common.updateRequest"),
          t("incidents.updatedSuccess"),
          Date.now,
        ).then((o) => {
          showSnack(o);
          refreshAfterMutation();
        }),
      )
      .catch(console.error);
  };

  const assignIncidentSubmitAction = (formJson: any) => {
    const incidentId = formJson.incidentId;
    const onSuccess = () =>
      IncidentModel.message(
        IncidentModel.SUCCESS,
        t("incidents.assignTitle"),
        t("incidents.assignSuccess"),
        Date.now,
      ).then((o) => {
        showSnack(o);
        refreshAfterMutation();
      });

    if (formJson.assignmentType === "user") {
      incidentService.assignToMe(incidentId).then(onSuccess).catch(console.error);
    } else if (formJson.assignmentType === "group") {
      incidentService
        .assignToGroup(incidentId, { assignedGroup: formJson.assignedGroup }, formJson.assignedGroup)
        .then(onSuccess)
        .catch(console.error);
    }
  };

  const statusIncidentSubmitAction = (formJson: any) => {
    incidentService
      .updateStatus(
        formJson.incidentId,
        { status: formJson.status, resolution: formJson.resolution || undefined },
        formJson.status,
      )
      .then(() =>
        IncidentModel.message(
          IncidentModel.SUCCESS,
          t("incidents.statusTitle"),
          t("incidents.statusSuccess"),
          Date.now,
        ).then((o) => {
          showSnack(o);
          refreshAfterMutation();
        }),
      )
      .catch(console.error);
  };

  const handleDelete = async (item: Incident) => {
    try {
      await confirm({ description: t("incidents.deleteConfirm", { name: item.id }) });
      await incidentService.deleteIncident(item.id);
      const opts = await IncidentModel.message(
        IncidentModel.SUCCESS,
        t("common.deleteRequest"),
        t("incidents.deletedSuccess"),
        Date.now,
      );
      showSnack(opts);
      refreshAfterMutation();
    } catch (e) {
      if (e) console.error(e);
    }
  };

  const handleSendWhatsApp = async (item: Incident) => {
    try {
      await confirm({
        title: t("incidents.sendWhatsAppConfirmTitle"),
        description: t("incidents.sendWhatsAppConfirm"),
      });
      setSendingWhatsAppId(item.id);
      const response = await incidentService.sendWhatsAppNotification(item.id);
      const opts = await IncidentModel.message(
        IncidentModel.SUCCESS,
        t("incidents.sendWhatsAppConfirmTitle"),
        t("incidents.sendWhatsAppSuccess", {
          recipient: response.recipient || whatsappRecipientFallback(item),
        }),
        Date.now,
      );
      showSnack(opts);
      refreshAfterMutation();
    } catch (e) {
      if (e) console.error(e);
    } finally {
      setSendingWhatsAppId(null);
    }
  };

  const handleSendEmail = async (item: Incident) => {
    try {
      await confirm({
        title: t("incidents.sendEmailConfirmTitle"),
        description: t("incidents.sendEmailConfirm"),
      });
      setSendingEmailId(item.id);
      const response = await incidentService.sendEmailNotification(item.id);
      const opts = await IncidentModel.message(
        IncidentModel.SUCCESS,
        t("incidents.sendEmailConfirmTitle"),
        t("incidents.sendEmailSuccess", {
          recipient: response.recipient || emailRecipientFallback(item),
        }),
        Date.now,
      );
      showSnack(opts);
      refreshAfterMutation();
    } catch (e) {
      if (e) console.error(e);
    } finally {
      setSendingEmailId(null);
    }
  };

  useEffect(() => {
    if (!isCompact || !workspace.selectedIncident) {
      setMobileDetailOpen(false);
    }
  }, [isCompact, workspace.selectedIncident]);

  const selectIncident = useCallback(
    (incident: Incident) => {
      workspace.setSelectedIncident(incident);
      if (isCompact) {
        setMobileDetailOpen(true);
      }
    },
    [isCompact, workspace.setSelectedIncident],
  );

  const readingPane = (
    <IncidentReadingPane
      incident={workspace.selectedIncident}
      selectedFolder={workspace.selectedFolder}
      onAssign={(item) =>
        assignIncidentDialogRef.current?.showDialog(IncidentModel.assignDialogOpts(t), item)
      }
      onCommentAdded={refreshAfterMutation}
      onStatus={(item) =>
        statusIncidentDialogRef.current?.showDialog(IncidentModel.statusDialogOpts(t), item)
      }
      onEdit={(item) =>
        editIncidentDialogRef.current?.showDialog(IncidentModel.editDialogOpts(t), item)
      }
      onDelete={handleDelete}
      onSendWhatsApp={handleSendWhatsApp}
      sendingWhatsApp={sendingWhatsAppId === workspace.selectedIncident?.id}
      onSendEmail={handleSendEmail}
      sendingEmail={sendingEmailId === workspace.selectedIncident?.id}
    />
  );

  const selectedFolder = INCIDENT_FOLDERS.find((f) => f.id === workspace.selectedFolder);
  const folderLabel = selectedFolder
    ? t(`incidents.folders.${selectedFolder.id}`)
    : t("incidents.title");

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <PageListContainer fillHeight flush paperSx={{ border: 0, borderRadius: 0, height: "100%" }}>
      <ProjectDialog ref={editIncidentDialogRef} submitAction={editIncidentSubmitAction} body={editIncidentBody} />
      <ProjectDialog ref={assignIncidentDialogRef} submitAction={assignIncidentSubmitAction} body={assignIncidentBody} />
      <ProjectDialog ref={statusIncidentDialogRef} submitAction={statusIncidentSubmitAction} body={statusIncidentBody} />

      <IncidentAdvancedFilters
        filters={workspace.filters}
        onChange={workspace.updateFilters}
        onClear={workspace.clearFilters}
        uniqueAssignees={workspace.uniqueAssignees}
        uniqueGroups={workspace.uniqueGroups}
        uniqueCategories={workspace.uniqueCategories}
        activeFiltersCount={workspace.activeFiltersCount}
        selectedCustomer={workspace.selectedCustomer}
        onCustomerChange={workspace.selectCustomer}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          overflow: "hidden",
          alignItems: "stretch",
        }}
      >
        <IncidentFolderSidebar
          selectedFolder={workspace.selectedFolder}
          folderCounts={workspace.folderCounts}
          onSelectFolder={workspace.selectFolder}
        />

        <IncidentListPanel
          loading={workspace.loading}
          incidents={workspace.paginatedIncidents}
          filteredCount={workspace.totalElements}
          selectedIncidentId={workspace.selectedIncident?.id}
          onSelectIncident={selectIncident}
          onRefresh={workspace.fetchIncidents}
          currentPage={workspace.currentPage}
          totalPages={workspace.totalPages}
          itemsPerPage={workspace.itemsPerPage}
          onPageChange={workspace.setCurrentPage}
          onItemsPerPageChange={(size) => {
            workspace.setItemsPerPage(size);
            workspace.setCurrentPage(1);
          }}
          folderLabel={folderLabel}
        />

        {isCompact ? null : readingPane}
      </Box>
      <Drawer
        anchor="right"
        open={isCompact && mobileDetailOpen}
        onClose={() => setMobileDetailOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "min(560px, 100%)" },
            maxWidth: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            minHeight: 56,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <IconButton
            aria-label={t("common.close")}
            onClick={() => setMobileDetailOpen(false)}
            sx={{ width: 44, height: 44 }}
          >
            <Close />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
            {workspace.selectedIncident?.title?.trim() || t("incidents.details")}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>{readingPane}</Box>
      </Drawer>
      </PageListContainer>
    </Box>
  );
}

export default IncidentsWorkspace;
